import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, LogOut, MapPin, MessageCircle, ShieldCheck, Sparkles, Loader2, AlertTriangle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useEnrichedMatches } from '../../hooks/useEnrichedMatches';
import { useUser } from '../../hooks/useUsers';
import { calculateCompatibilityScore } from '../../utils/compatibility';
import BottomNav from '../../components/BottomNav';
import TenantNav from '../../components/TenantNav';

type FilterType = 'all' | 'recent' | 'high-match';

const Matches: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser, logout } = useAuth();
  const { user: currentUserData } = useUser(currentUser?.uid);
  const { enrichedMatches, loading, error, indexBuilding } = useEnrichedMatches();
  const [filter, setFilter] = useState<FilterType>('all');

  // Calculate compatibility scores and prepare display data
  const matchesWithScores = useMemo(() => {
    if (!currentUserData || !enrichedMatches.length) return [];
    
    return enrichedMatches.map(match => ({
      ...match,
      compatibilityScore: calculateCompatibilityScore(currentUserData, match.user),
    }));
  }, [enrichedMatches, currentUserData]);

  // Filter matches
  const filteredMatches = useMemo(() => {
    if (filter === 'all') return matchesWithScores;
    if (filter === 'recent') {
      return [...matchesWithScores].sort((a, b) => 
        (b?.lastMessageAt?.toMillis?.() || 0) - (a?.lastMessageAt?.toMillis?.() || 0)
      );
    }
    if (filter === 'high-match') {
      return [...matchesWithScores].sort((a, b) => 
        (b?.compatibilityScore || 0) - (a?.compatibilityScore || 0)
      );
    }
    return matchesWithScores;
  }, [matchesWithScores, filter]);

  let errorMessage = 'Something went wrong loading your matches. Please refresh and try again.';
  if (indexBuilding) {
    errorMessage = 'Your matches are being prepared and will appear here shortly. This usually takes a few minutes.';
  } else if (error === 'PERMISSION_DENIED') {
    errorMessage = "We couldn't access your matches. Try logging out and back in.";
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50 px-2 sm:px-4 pt-4 pb-24 md:pb-10">
        <div className="max-w-6xl mx-auto space-y-4">
          <TenantNav />
          <div className="flex items-center justify-center min-h-[60vh]">
            <div className="text-center">
              <Loader2 className="w-12 h-12 text-primary-300 animate-spin mx-auto mb-4" />
              <p className="text-text-secondary font-semibold">Loading your matches...</p>
            </div>
          </div>
        </div>
        <BottomNav />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50 px-2 sm:px-4 pt-4 pb-24 md:pb-10">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-28 -left-24 w-72 h-72 bg-primary-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
        <div className="absolute bottom-10 right-10 w-64 h-64 bg-accent-purple rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
      </div>

      <div className="relative max-w-6xl mx-auto space-y-4 md:space-y-6">
        <TenantNav />
        {/* Header - mobile responsive */}
        <div className="flex flex-wrap items-start justify-between gap-3 md:gap-4">
          <div>
            <p className="text-xs sm:text-sm uppercase tracking-wide text-text-muted font-semibold">Matches</p>
            <h1 className="text-2xl sm:text-3xl md:text-5xl font-extrabold text-text-primary">
              People vibing with you
            </h1>
            <p className="text-text-secondary mt-1 md:mt-2 text-sm md:text-base">
              Connect, chat, and find your perfect roommate match.
            </p>
          </div>
          <div className="hidden sm:flex px-3 md:px-5 py-2 md:py-3 rounded-xl md:rounded-2xl bg-white border-2 border-primary-100 shadow-lg text-primary-300 font-semibold items-center gap-2 text-sm md:text-base">
            <Sparkles className="w-4 h-4 md:w-5 md:h-5" /> {matchesWithScores.length} matches
          </div>
        </div>

        {/* Filter chips - mobile optimized */}
        <div className="flex flex-wrap gap-2 md:gap-3 overflow-x-auto pb-2">
          {[
            { key: 'all', label: 'All' },
            { key: 'recent', label: 'Recent' },
            { key: 'high-match', label: 'Top Match' }
          ].map((option) => (
            <button
              key={option.key}
              type="button"
              onClick={() => setFilter(option.key as FilterType)}
              className={`px-3 md:px-4 py-1.5 md:py-2 rounded-full text-xs md:text-sm font-semibold border-2 transition-all duration-200 whitespace-nowrap ${
                filter === option.key
                  ? 'bg-primary-200 text-white border-primary-300 shadow-md'
                  : 'bg-white text-text-primary border-gray-200 hover:border-primary-200'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>

        {/* Error state - surfaces query failures instead of a misleading empty state */}
        {error && (
          <div className="card p-6 md:p-8 text-center border-2 border-red-100">
            <AlertTriangle className="w-12 h-12 md:w-16 md:h-16 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl md:text-2xl font-bold text-text-primary mb-2">
              {indexBuilding ? 'Setting things up' : "Couldn't load your matches"}
            </h2>
            <p className="text-text-secondary mb-6 text-sm md:text-base">{errorMessage}</p>
            <button className="btn-primary" onClick={() => window.location.reload()}>
              Retry
            </button>
          </div>
        )}

        {/* Matches grid - responsive */}
        {!error && filteredMatches.length === 0 && (
          <div className="card p-6 md:p-8 text-center">
            <Heart className="w-12 h-12 md:w-16 md:h-16 text-primary-300 mx-auto mb-4" />
            <h2 className="text-xl md:text-2xl font-bold text-text-primary mb-2">No matches yet</h2>
            <p className="text-text-secondary mb-6 text-sm md:text-base">
              Start swiping to find your perfect roommate!
            </p>
            <button className="btn-primary" onClick={() => navigate('/tenant/swipe')}>
              Discover Roommates
            </button>
          </div>
        )}

        {!error && filteredMatches.length > 0 && (
          <div className="grid sm:grid-cols-2 gap-3 md:gap-5">
            {filteredMatches.map((match) => (
              <div key={match?.matchId} className="card p-4 md:p-5 space-y-3 md:space-y-4 border border-gray-100">
                {/* Header */}
                <div className="flex items-start justify-between gap-2 md:gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h2 className="text-lg sm:text-xl md:text-2xl font-extrabold text-text-primary truncate">
                        {match?.user.name}
                      </h2>
                      <span className="px-2 md:px-3 py-0.5 md:py-1 rounded-full bg-primary-50 text-primary-300 text-[10px] md:text-xs font-semibold whitespace-nowrap">
                        {match?.user.age} years
                      </span>
                    </div>
                    <p className="text-text-secondary flex items-center gap-1.5 text-xs md:text-sm mt-1">
                      <MapPin className="w-3 h-3 md:w-4 md:h-4 flex-shrink-0" /> {match?.user.city}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-[10px] md:text-sm text-text-secondary">Match</p>
                    <p className="text-xl md:text-2xl font-extrabold text-text-primary">
                      {match?.compatibilityScore}%
                    </p>
                  </div>
                </div>

                {/* Stats grid */}
                <div className="grid grid-cols-2 gap-2 md:gap-3 text-xs md:text-sm">
                  <div className="p-2 md:p-3 rounded-lg md:rounded-xl bg-primary-50 border-2 border-primary-100">
                    <p className="text-text-secondary text-[10px] md:text-xs">Budget (share)</p>
                    <p className="text-text-primary font-semibold text-xs md:text-sm">
                      R{match?.user.budgetMin ? Math.floor(match.user.budgetMin/1000) : 0}k-{match?.user.budgetMax ? Math.floor(match.user.budgetMax/1000) : 0}k
                    </p>
                  </div>
                  <div className="p-2 md:p-3 rounded-lg md:rounded-xl bg-accent-blue/10 border-2 border-accent-blue/30">
                    <p className="text-text-secondary text-[10px] md:text-xs">Lifestyle</p>
                    <p className="text-text-primary font-semibold text-xs md:text-sm truncate">
                      {match?.user.lifestyle?.[0] || 'Flexible'}
                    </p>
                  </div>
                </div>

                {/* Bio */}
                <p className="text-text-secondary text-xs md:text-sm line-clamp-2">
                  {match?.user.bio}
                </p>

                {/* Interests tags */}
                <div className="flex flex-wrap gap-1.5 md:gap-2">
                  {match?.user.interests?.slice(0, 3).map((interest: string) => (
                    <span
                      key={interest}
                      className="px-2 md:px-3 py-0.5 md:py-1 rounded-full text-[10px] md:text-xs font-semibold bg-white border-2 border-primary-100 text-text-primary"
                    >
                      {interest}
                    </span>
                  ))}
                </div>

                {/* Action buttons - mobile optimized */}
                <div className="flex flex-col sm:flex-row gap-2 md:gap-3">
                  <button
                    type="button"
                    onClick={() => navigate(`/tenant/chat/${match?.matchId}`)}
                    className="btn-primary flex-1 flex items-center justify-center gap-2 text-sm md:text-base py-2.5 md:py-3"
                  >
                    <MessageCircle className="w-4 h-4" /> Chat
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate(`/tenant/matches/${match?.matchId}/profile`)}
                    className="btn-secondary flex-1 flex items-center justify-center gap-2 text-sm md:text-base py-2.5 md:py-3"
                  >
                    <Heart className="w-4 h-4" /> View Profile
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Bottom CTA - mobile responsive */}
        <div className="card p-4 md:p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 md:gap-4">
          <div className="flex items-start gap-3">
            <ShieldCheck className="w-5 h-5 text-primary-300 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-text-primary font-semibold text-sm md:text-base">Quality matches</p>
              <p className="text-xs md:text-sm text-text-secondary">
                Based on budget, lifestyle, and compatibility
              </p>
            </div>
          </div>
          <button
            className="btn-primary w-full sm:w-auto text-sm md:text-base whitespace-nowrap"
            onClick={() => navigate('/tenant/swipe')}
          >
            Find more matches
          </button>
        </div>

        {/* Logout Button */}
        <div className="mt-8 pb-6">
          <button
            onClick={async () => {
              await logout();
              navigate('/login');
            }}
            className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-white/80 backdrop-blur-md border border-gray-200 rounded-2xl text-gray-700 font-semibold hover:bg-red-50 hover:border-red-300 hover:text-red-600 transition-all duration-200 shadow-sm hover:shadow-md"
          >
            <LogOut className="w-5 h-5" />
            Logout
          </button>
        </div>
      </div>

      <BottomNav />
    </div>
  );
};

export default Matches;
